import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  CircularProgress,
  Dialog,
  MobileStepper,
  Typography,
} from '@material-ui/core';
import { useOnboardingState } from '../hooks/useOnboardingState';
import { WelcomeStep } from './steps/WelcomeStep';
import { DocsAckStep } from './steps/DocsAckStep';
import { TeamSelectStep } from './steps/TeamSelectStep';
import { CompleteStep } from './steps/CompleteStep';

const STEPS = ['welcome', 'docs-ack', 'team-select', 'complete'] as const;
type StepId = (typeof STEPS)[number];

const STEP_LABELS = ['Welcome', 'Documentation', 'Team', 'Complete'];

const useStyles = makeStyles(theme => ({
  dialog: {
    background: theme.palette.background.default,
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  header: {
    padding: theme.spacing(2, 3, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  stepper: {
    flexGrow: 1,
    background: 'transparent',
  },
  content: {
    flexGrow: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: 16,
  },
}));

interface Props {
  /** Called after onboarding is successfully completed. Use to unmount the wizard and navigate. */
  onDone?: () => void;
}

export function OnboardingWizard({ onDone }: Props = {}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    isLoading,
    currentStep,
    docsAcked,
    teamSlug,
    updateStep,
    ackDoc,
    joinTeam,
    createTeam,
    completeOnboarding,
  } = useOnboardingState();

  const activeIndex = Math.max(
    0,
    STEPS.indexOf(currentStep as StepId),
  );

  const advanceTo = useCallback(
    async (next: StepId) => {
      await updateStep(next);
    },
    [updateStep],
  );

  if (isLoading) {
    return (
      <Box className={classes.loadingRoot}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading your onboarding status…
        </Typography>
      </Box>
    );
  }

  return (
    <Dialog
      open
      fullScreen
      PaperProps={{ className: classes.dialog }}
      // Prevent escape / backdrop click from dismissing
      disableEscapeKeyDown
    >
      <Box className={classes.inner}>
        {/* Step progress header */}
        <Box className={classes.header}>
          <Typography variant="subtitle2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
            Step {activeIndex + 1} of {STEPS.length}
          </Typography>
          <MobileStepper
            variant="dots"
            steps={STEPS.length}
            position="static"
            activeStep={activeIndex}
            className={classes.stepper}
            backButton={null}
            nextButton={null}
          />
          <Typography variant="subtitle2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
            {STEP_LABELS[activeIndex]}
          </Typography>
        </Box>

        {/* Current step content */}
        <Box className={classes.content}>
          {currentStep === 'welcome' && (
            <WelcomeStep onNext={() => advanceTo('docs-ack')} />
          )}

          {currentStep === 'docs-ack' && (
            <DocsAckStep
              docsAcked={docsAcked}
              onAckDoc={ackDoc}
              onNext={() => advanceTo('team-select')}
            />
          )}

          {currentStep === 'team-select' && (
            <TeamSelectStep
              currentTeamSlug={teamSlug}
              onJoinTeam={joinTeam}
              onCreateTeam={createTeam}
              onNext={() => advanceTo('complete')}
            />
          )}

          {currentStep === 'complete' && (
            <CompleteStep
              teamSlug={teamSlug}
              onComplete={completeOnboarding}
              onDone={onDone ?? (() => navigate('/'))}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
